import * as React from "react"

import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog"
import {
  IconAlertOctagonFilled,
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconInfoCircleFilled,
  IconLoader,
} from "@tabler/icons-react"
import { useSelector } from "@tanstack/react-store"
import { Store } from "@tanstack/store"

import { cn } from "@/lib/utils"

import { Button } from "./button"

function AlertDialog(props: AlertDialogPrimitive.Root.Props) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
}

function AlertDialogTrigger(props: AlertDialogPrimitive.Trigger.Props) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
}

function AlertDialogPortal(props: AlertDialogPrimitive.Portal.Props) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
}

function AlertDialogOverlay({
  className,
  ...props
}: AlertDialogPrimitive.Backdrop.Props) {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-overlay"
      forceRender
      className={cn(
        "fixed inset-0 z-modal bg-black/50 data-[closed]:animate-out data-[closed]:animation-duration-[200ms] data-[closed]:fade-out-0 data-[open]:animate-in data-[open]:fade-in-0",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogContent({ className, ...props }: AlertDialogPrimitive.Popup.Props) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Popup
        data-slot="alert-dialog-content"
        className={cn(
          "fixed top-[50%] left-[50%] z-modal grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-popover p-6 shadow-lg duration-200 data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95 sm:max-w-lg",
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({ className, ...props }: AlertDialogPrimitive.Title.Props) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold", className)}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: AlertDialogPrimitive.Description.Props) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function AlertDialogAction({ className, ...props }: AlertDialogPrimitive.Close.Props) {
  return (
    <AlertDialogPrimitive.Close
      className={className}
      render={<Button variant="primary" />}
      {...props}
    />
  )
}

function AlertDialogCancel({ className, ...props }: AlertDialogPrimitive.Close.Props) {
  return (
    <AlertDialogPrimitive.Close
      className={className}
      render={<Button variant="neutral" />}
      {...props}
    />
  )
}

/**
 * Declarative shorthand for the common trigger + title / description /
 * cancel / confirm shape. When the confirmation is spawned from an event
 * handler rather than a trigger element in the tree (or you want the choice
 * back as a promise), reach for `alertDialog.confirm` below instead.
 */
function AlertDialogSimple({
  title,
  cancelLabel = "Cancel",
  confirmLabel = "Confirm",
  onConfirm,
  description,
  triggerRender,
  children,
}: {
  title: string
  description: React.ReactNode
  cancelLabel?: string
  confirmLabel?: string
  onConfirm?: () => void
  triggerRender?: AlertDialogPrimitive.Trigger.Props["render"]
  children?: React.ReactNode
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger render={triggerRender}>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ---------------------------------------------------------------------------
// Imperative API — `alertDialog`, mirroring the `toast` singleton
// ---------------------------------------------------------------------------

type AlertDialogId = number

type AlertDialogNoticeType = "success" | "error" | "message" | "caution"

interface AlertDialogLoadingOptions {
  /** Body text rendered under the title. */
  description?: React.ReactNode
}

interface AlertDialogNoticeOptions extends AlertDialogLoadingOptions {
  /** Label of the acknowledge button. Defaults to "OK". */
  confirmLabel?: string
}

interface AlertDialogConfirmOptions extends AlertDialogLoadingOptions {
  /** Label of the confirm button. Defaults to "Confirm". */
  confirmLabel?: string
  /** Label of the cancel button. Defaults to "Cancel". */
  cancelLabel?: string
  /** Render the confirm button as dangerous, for irreversible actions. */
  destructive?: boolean
}

type AlertDialogEntryState =
  | ({ kind: "confirm"; title: React.ReactNode } & AlertDialogConfirmOptions)
  | ({
      kind: "notice"
      type: AlertDialogNoticeType
      title: React.ReactNode
    } & AlertDialogNoticeOptions)
  | ({ kind: "loading"; title: React.ReactNode } & AlertDialogLoadingOptions)

interface AlertDialogEntry {
  id: AlertDialogId
  /** Flips false to play the exit animation; the entry is removed once it completes. */
  open: boolean
  state: AlertDialogEntryState
  /** Settles the promise handed to the spawner. Fired at most once, while `open`. */
  resolve?: (confirmed: boolean) => void
}

// Module-level store consumed by `AlertDialogContainer`, so dialogs can be
// spawned from anywhere (event handlers, promise chains) without a provider.
// Snapshots are immutable: every change replaces the array.
const alertDialogStore = new Store<AlertDialogEntry[]>([])
let nextAlertDialogId = 1

function spawnAlertDialog(
  state: AlertDialogEntryState,
  resolve?: (confirmed: boolean) => void,
): AlertDialogId {
  const id = nextAlertDialogId++
  alertDialogStore.setState((entries) => [
    ...entries,
    { id, open: true, state, resolve },
  ])
  return id
}

function findAlertDialog(id: AlertDialogId) {
  return alertDialogStore.state.find((entry) => entry.id === id)
}

function patchAlertDialog(id: AlertDialogId, patch: Partial<AlertDialogEntry>) {
  alertDialogStore.setState((entries) =>
    entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
  )
}

/** Resolves the entry's promise and starts its exit animation. Idempotent. */
function settleAlertDialog(id: AlertDialogId, confirmed: boolean) {
  const entry = findAlertDialog(id)
  if (!entry || !entry.open) return
  entry.resolve?.(confirmed)
  patchAlertDialog(id, { open: false })
}

function removeAlertDialog(id: AlertDialogId) {
  if (!findAlertDialog(id)) return
  alertDialogStore.setState((entries) => entries.filter((entry) => entry.id !== id))
}

function confirmAlertDialog(
  title: React.ReactNode,
  options?: AlertDialogConfirmOptions,
): Promise<boolean> {
  return new Promise((resolve) => {
    spawnAlertDialog({ kind: "confirm", title, ...options }, resolve)
  })
}

const createAlertDialogNoticeHandler =
  (type: AlertDialogNoticeType) =>
  (title: React.ReactNode, options?: AlertDialogNoticeOptions): Promise<void> =>
    new Promise((resolve) => {
      spawnAlertDialog({ kind: "notice", type, title, ...options }, () => resolve())
    })

function loadingAlertDialog(
  title: React.ReactNode,
  options?: AlertDialogLoadingOptions,
): AlertDialogId {
  return spawnAlertDialog({ kind: "loading", title, ...options })
}

function dismissAlertDialog(id?: AlertDialogId) {
  const ids = id !== undefined ? [id] : alertDialogStore.state.map((entry) => entry.id)
  for (const entryId of ids) settleAlertDialog(entryId, false)
}

type AlertDialogPromiseMessages<T> = {
  loading: React.ReactNode
  /** Title of the success notice, or `null` to close silently on success. */
  success: React.ReactNode | ((data: T) => React.ReactNode) | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callers often access `.message` on the error
  error: React.ReactNode | ((reason: any) => React.ReactNode)
}

type AlertDialogPromiseOptions = {
  loading?: AlertDialogLoadingOptions
  success?: AlertDialogNoticeOptions
  error?: AlertDialogNoticeOptions
}

function resolveAlertDialogTitle<A>(
  value: React.ReactNode | ((arg: A) => React.ReactNode),
  arg: A,
): React.ReactNode {
  return typeof value === "function" ? value(arg) : value
}

function promiseAlertDialog<T>(
  p: Promise<T>,
  msgs: AlertDialogPromiseMessages<T>,
  opts?: AlertDialogPromiseOptions,
): Promise<T> {
  const id = spawnAlertDialog({
    kind: "loading",
    title: msgs.loading,
    ...opts?.loading,
  })

  // Swap the still-open loading dialog to the outcome in place (no overlay
  // flicker); if it was closed mid-flight, show the outcome as a fresh dialog.
  const morph = (state: AlertDialogEntryState) => {
    if (findAlertDialog(id)?.open) {
      patchAlertDialog(id, { state })
    } else {
      spawnAlertDialog(state)
    }
  }

  p.then(
    (data) => {
      if (msgs.success == null) {
        dismissAlertDialog(id)
      } else {
        morph({
          kind: "notice",
          type: "success",
          title: resolveAlertDialogTitle(msgs.success, data),
          ...opts?.success,
        })
      }
    },
    (reason: unknown) => {
      morph({
        kind: "notice",
        type: "error",
        title: resolveAlertDialogTitle(msgs.error, reason),
        ...opts?.error,
      })
    },
  )

  return p
}

/**
 * Drops all imperative alert dialogs synchronously, resolving pending
 * `confirm`s with `false`. Test cleanup only — in the app, dialogs always
 * animate out through `alertDialog.dismiss`.
 */
function resetAlertDialogs() {
  // Entries mid-exit-animation (`open: false`) already resolved when settled.
  for (const entry of alertDialogStore.state) if (entry.open) entry.resolve?.(false)
  alertDialogStore.setState(() => [])
}

/**
 * Imperative alert-dialog singleton, mirroring the `toast` API
 * (`@/components/ui/cn/toast`): spawn modal dialogs straight from event
 * handlers and async flows, without wiring open-state or triggers into the
 * tree. Requires `<AlertDialogContainer />` mounted once at the app root
 * (`web/src/index.tsx`), just like `ToastContainer`.
 *
 * - `confirm` resolves `true`/`false` with the user's choice; Escape counts
 *   as cancel.
 * - `success` / `error` / `message` / `caution` are acknowledge-only notices
 *   that resolve once dismissed — for outcomes the user must see, where a
 *   toast would be too easy to miss.
 * - `loading` blocks interaction while work is in flight (Escape is vetoed,
 *   mirroring non-dismissable loading toasts); close it with `dismiss(id)`.
 * - `promise` shows a loading dialog while the promise runs, then swaps it to
 *   a success/error notice in place; pass `success: null` to close silently
 *   when it fulfills.
 *
 * Composes with `toast`: gate an action with `confirm`, then report progress
 * with `toast.promise` when the user doesn't need to acknowledge the outcome.
 */
const alertDialog = {
  confirm: confirmAlertDialog,
  success: createAlertDialogNoticeHandler("success"),
  error: createAlertDialogNoticeHandler("error"),
  message: createAlertDialogNoticeHandler("message"),
  caution: createAlertDialogNoticeHandler("caution"),
  loading: loadingAlertDialog,
  promise: promiseAlertDialog,
  dismiss: dismissAlertDialog,
}

const NOTICE_ICON_CLASSES: Record<AlertDialogNoticeType, string> = {
  success: /*tw*/ "text-success",
  error: /*tw*/ "text-destructive",
  message: /*tw*/ "text-foreground",
  caution: /*tw*/ "text-warning",
}

const NOTICE_ICONS: Record<AlertDialogNoticeType, typeof IconCircleCheckFilled> = {
  success: IconCircleCheckFilled,
  error: IconAlertOctagonFilled,
  message: IconInfoCircleFilled,
  caution: IconAlertTriangleFilled,
}

function AlertDialogEntryIcon({ state }: { state: AlertDialogEntryState }) {
  if (state.kind === "loading") {
    return <IconLoader size={20} className="shrink-0 animate-spin" />
  }
  if (state.kind === "notice") {
    const TypeIcon = NOTICE_ICONS[state.type]
    return (
      <TypeIcon size={20} className={cn("shrink-0", NOTICE_ICON_CLASSES[state.type])} />
    )
  }
  return null
}

function AlertDialogEntryFooter({ entry }: { entry: AlertDialogEntry }) {
  const { id, state } = entry
  if (state.kind === "loading") return null
  if (state.kind === "confirm") {
    return (
      <AlertDialogFooter>
        <Button variant="neutral" onClick={() => settleAlertDialog(id, false)}>
          {state.cancelLabel ?? "Cancel"}
        </Button>
        <Button
          variant={state.destructive ? "danger" : "primary"}
          onClick={() => settleAlertDialog(id, true)}
        >
          {state.confirmLabel ?? "Confirm"}
        </Button>
      </AlertDialogFooter>
    )
  }
  return (
    <AlertDialogFooter>
      <Button variant="primary" onClick={() => settleAlertDialog(id, true)}>
        {state.confirmLabel ?? "OK"}
      </Button>
    </AlertDialogFooter>
  )
}

function ImperativeAlertDialog({ entry }: { entry: AlertDialogEntry }) {
  const { id, open, state } = entry

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen, eventDetails) => {
        if (nextOpen) return
        // Loading dialogs track in-flight work, so Escape must not hide them —
        // it wouldn't cancel the work, just lose the progress indicator.
        // `alertDialog.dismiss(id)` still closes them programmatically.
        if (state.kind === "loading") {
          eventDetails.cancel()
          return
        }
        settleAlertDialog(id, false)
      }}
      onOpenChangeComplete={(nextOpen) => {
        if (!nextOpen) removeAlertDialog(id)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertDialogEntryIcon state={state} />
            {state.title}
          </AlertDialogTitle>
          {state.description != null && (
            <AlertDialogDescription>{state.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogEntryFooter entry={entry} />
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * Renders the dialogs spawned through `alertDialog`. Mount once at the app
 * root, next to `ToastContainer`. Later spawns stack on top of earlier ones.
 */
function AlertDialogContainer() {
  const entries = useSelector(alertDialogStore)
  return (
    <>
      {entries.map((entry) => (
        <ImperativeAlertDialog key={entry.id} entry={entry} />
      ))}
    </>
  )
}

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogSimple,
  AlertDialogContainer,
  alertDialog,
  resetAlertDialogs,
  type AlertDialogId,
  type AlertDialogConfirmOptions,
  type AlertDialogNoticeOptions,
  type AlertDialogLoadingOptions,
  type AlertDialogPromiseMessages,
  type AlertDialogPromiseOptions,
}
